import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import { motiveDocumentSchema, type MotiveDocument } from "../model/types";

const MEDIA_TYPE = "application/vnd.modelmotive.project+zip";
const MAX_ARCHIVE_BYTES = 50 * 1024 * 1024;
const MAX_EXPANDED_BYTES = 100 * 1024 * 1024;
const MAX_ENTRIES = 128;

function safeFilename(name: string): string {
  return name.trim().replace(/[^A-Za-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "") || "model";
}

export function createMotivePackage(document: MotiveDocument): Blob {
  const parsed = motiveDocumentSchema.parse(document);
  const manifest = {
    mediaType: MEDIA_TYPE,
    schemaVersion: parsed.schemaVersion,
    projectId: parsed.id,
    name: parsed.name,
    document: "document.json",
    createdAt: parsed.metadata.createdAt,
    modifiedAt: parsed.metadata.modifiedAt,
    generator: `ModelMotive ${parsed.applicationVersion}`
  };
  const archive = zipSync(
    {
      "manifest.json": strToU8(JSON.stringify(manifest, null, 2)),
      "document.json": strToU8(JSON.stringify(parsed, null, 2))
    },
    { level: 6 }
  );
  return new Blob([archive], { type: MEDIA_TYPE });
}

export async function readMotivePackage(file: Blob): Promise<MotiveDocument> {
  if (file.size > MAX_ARCHIVE_BYTES) throw new Error("The project archive is larger than 50 MB");
  const files = unzipSync(new Uint8Array(await file.arrayBuffer()));
  const names = Object.keys(files);
  if (names.length > MAX_ENTRIES) throw new Error("The project archive contains too many files");
  let expanded = 0;
  for (const [name, value] of Object.entries(files)) {
    if (name.startsWith("/") || name.includes("..") || name.includes("\\")) throw new Error("The project archive contains an unsafe path");
    expanded += value.byteLength;
  }
  if (expanded > MAX_EXPANDED_BYTES) throw new Error("The expanded project archive is too large");

  const manifestBytes = files["manifest.json"];
  const documentBytes = files["document.json"];
  if (!manifestBytes || !documentBytes) throw new Error("The project archive is missing manifest.json or document.json");
  const manifest = JSON.parse(strFromU8(manifestBytes)) as { mediaType?: string; schemaVersion?: number };
  if (manifest.mediaType !== MEDIA_TYPE) throw new Error("This is not a ModelMotive project archive");
  if (manifest.schemaVersion !== 1) throw new Error(`Unsupported ModelMotive schema version: ${manifest.schemaVersion ?? "unknown"}`);
  return motiveDocumentSchema.parse(JSON.parse(strFromU8(documentBytes)));
}

export function motiveFilename(document: MotiveDocument): string {
  return `${safeFilename(document.name)}.motive`;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 2_000);
}

export async function saveBlobWithPicker(blob: Blob, suggestedName: string, description: string, extension: string): Promise<void> {
  const picker = (window as typeof window & {
    showSaveFilePicker?: (options: unknown) => Promise<FileSystemFileHandle>;
  }).showSaveFilePicker;
  if (picker) {
    const handle = await picker({
      suggestedName,
      types: [{ description, accept: { [blob.type || "application/octet-stream"]: [extension] } }]
    });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
    return;
  }
  downloadBlob(blob, suggestedName);
}
